<template>
  <panel-layout
    id="supplier-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #heading>
      <div v-if="supplier" class="flex items-center space-x-2">
        <div>
          <h2 class="text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none dark:text-gray-100">
            {{ supplier.isNewRecord ? $t('supplier.new') : supplier.name }}
          </h2>
          <div v-if="!supplier.isNewRecord" class="mt-1">
            <p class="text-gray-700 text-sm md:text-base dark:text-gray-200" v-html="$t('pages.supplier.edit.id', { id: supplier.idSupplier })" />
          </div>
        </div>
      </div>
    </template>

    <o-form v-if="supplier" ref="form" class="h-full w-full">
      <o-scroll class="h-full w-full">
        <div class="w-full px-6 py-4">
        <div class="flex space-x-4 mb-6">
          <o-field-group v-slot="{ status }" rules="required" class="w-full" :label="$t('general.title')" :info="$t('pages.supplier.edit.companyName')">
            <text-field v-model="supplier.name" :label="$t('general.title')" :status="status" />
          </o-field-group>
        </div>

        <div class="flex space-x-4 mb-6">
          <o-field-group v-slot="{ status }" rules="required" class="w-full md:w-1/2" :label="$t('general.contact')">
            <text-field v-model="supplier.contact" :label="$t('general.contact')" :status="status" />
          </o-field-group>

          <o-field-group v-slot="{ status }" class="w-full md:w-1/2" :label="$t('general.email')">
            <text-field v-model="supplier.email" type="email" :label="$t('general.email')" :status="status" />
          </o-field-group>
        </div>

        <div class="flex space-x-4 mb-6">
          <o-field-group v-slot="{ status }" class="w-full md:w-1/2" :label="$t('general.phone')">
            <text-field v-model="supplier.phone" :label="$t('general.phone')" :status="status" />
          </o-field-group>

          <o-field-group v-slot="{ status }" class="w-full md:w-1/2" :label="$t('pages.supplier.edit.fax')">
            <text-field v-model="supplier.fax" :label="$t('pages.supplier.edit.fax')" :status="status" />
          </o-field-group>
        </div>

        <div class="flex space-x-4 mb-6">
          <o-field-group v-slot="{ status }" class="w-full md:w-1/2" :label="$t('general.website')">
            <text-field v-model="supplier.website" :label="$t('general.website')" :status="status" />
          </o-field-group>
        </div>

        <div class="flex space-x-4 mb-6">
          <o-field-group class="w-full" :label="$t('general.address')">
            <text-area-field v-model="supplier.address" :label="$t('general.address')" rows="5" />
          </o-field-group>
        </div>

        <div class="flex space-x-4 mb-6">
          <o-field-group v-slot="{ status }" class="w-full" :label="$t('pages.supplier.edit.paymentTerms')">
            <text-field v-model="supplier.paymentTerms" :label="$t('pages.supplier.edit.paymentTerms')" :status="status" />
          </o-field-group>
        </div>

        <div class="flex space-x-4 mb-6">
          <o-field-group class="w-full" :label="$t('general.notes')">
            <text-area-field v-model="supplier.notes" :label="$t('general.notes')" rows="5" />
          </o-field-group>
        </div>
        </div>
      </o-scroll>
    </o-form>

    <template #footer>
      <div v-if="supplier">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button variant="primary" outline icon="save" class="mr-2" @click="save()">
              {{ $t('general.save') }}
            </o-button>
            <o-button variant="primary" icon="save" class="mr-2" @click="save(true)">
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'
// @ts-ignore
import { TextField, TextAreaField } from '@/components/fields'

import { usePanel } from '~/composables/usePanel'

const { $api, $notification, $bus, $i18n } = useNuxtApp()

const props = defineProps<{ id?: string }>()

const panel = usePanel()
const confirmDialog = ref<any>(null)
const form = ref<any>()

const supplier = ref<any>(null)
const errors = ref(false)
const loading = ref(true)

const { unsavedChanges, snapshot } = useUnsavedChanges(supplier)

async function init(id?: string) {
  loading.value = true
  try {
    if (!id) {
      supplier.value = (await $api.supplier.defaults({} as any)).data
    } else {
      supplier.value = (await $api.supplier.get(id)).data
    }
    snapshot()
  } catch (e) {
    console.log(e)
  }
  loading.value = false
}

watch(() => props.id, (id) => init(id), { immediate: true })

async function save (close = false) {
  if (!await form.value?.validate()) return false

  loading.value = true
  errors.value = false

  try {
    const _supplier = (await $api.supplier.save(supplier.value)).data

    if (_supplier) {
      supplier.value = _supplier
      snapshot()

      $notification.toast({
        title: $i18n.t('pages.supplier.edit.success'),
        text: $i18n.t('pages.supplier.edit.successInfo'),
        variant: 'success',
      })

      $bus.emit.updated('Supplier', _supplier)

      if (close) {
        panel.close()
      }
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
    loading.value = false
    return false
  }

  loading.value = false
}

function cancel (force = false) {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

defineExpose({ save, cancel, unsavedChanges })
</script>
